import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, PageHeader, LoadingScreen, EmptyState } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

const MAX_UNIVERSITY_ADMINS = 3;

function ProfileModal({ user, onClose, s }) {
  const color = user.admin_type === 'university' ? COLORS.purple : COLORS.amber;
  const rows = [
    { label: 'Email',      value: user.email },
    { label: 'Type',       value: user.admin_type === 'university' ? 'University Admin' : 'Department Admin' },
    { label: 'Department', value: user.department || (user.admin_type === 'university' ? 'All Departments' : '—') },
    { label: 'Status',     value: user.is_active === false ? 'Inactive' : 'Active' },
    { label: 'Joined',     value: user.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—' },
  ];
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}
      onClick={onClose}>
      <div style={{ ...s.card, width:'100%', maxWidth:420, padding:0, overflow:'hidden', animation:'scaleIn 0.2s ease-out' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding:'24px 24px 18px', background:`linear-gradient(135deg,${color}22,${color}08)`, borderBottom:`1px solid ${color}33`, position:'relative' }}>
          <button onClick={onClose} style={{ position:'absolute', top:12, right:12, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, color:'#fff', cursor:'pointer', width:28, height:28, fontSize:16, display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:56, height:56, borderRadius:'50%', background:`linear-gradient(135deg,${color}55,${color}22)`, border:`2px solid ${color}66`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:900, color:'#fff', flexShrink:0 }}>
              {user.name?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <div style={{ fontSize:17, fontWeight:800, color:'#fff' }}>{user.name}</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', marginTop:2 }}>{user.email}</div>
              <div style={{ display:'flex', gap:6, marginTop:7, flexWrap:'wrap' }}>
                <span style={{ fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20, background:`${color}33`, color, border:`1px solid ${color}55` }}>
                  🛡️ ADMIN
                </span>
                <span style={{ fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20, background:'rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.7)', border:'1px solid rgba(255,255,255,0.2)' }}>
                  {(user.admin_type || '').toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div style={{ display:'flex', borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
          {[
            { label:'Verified',   value: user.verified_count   ?? 0, color: COLORS.blue  },
            { label:'Rejected',   value: user.rejected_count   ?? 0, color: '#f87171'    },
            { label:'Negligence', value: user.negligence_count ?? 0, color: COLORS.amber },
          ].map(st => (
            <div key={st.label} style={{ flex:1, padding:'12px 0', textAlign:'center', borderRight:'1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ fontSize:20, fontWeight:800, color:st.color }}>{st.value}</div>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginTop:2, textTransform:'uppercase', letterSpacing:0.5 }}>{st.label}</div>
            </div>
          ))}
        </div>
        <div style={{ padding:'16px 24px 22px' }}>
          {rows.map(({ label, value }) => (
            <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ fontSize:11, color:'rgba(255,255,255,0.4)', fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>{label}</span>
              <span style={{ fontSize:13, color:'#fff', fontWeight:600, maxWidth:220, textAlign:'right', wordBreak:'break-all' }}>{value}</span>
            </div>
          ))}
          {(user.negligence_count > 0) && (
            <div style={{ marginTop:12, padding:'7px 12px', borderRadius:8, background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.3)', fontSize:12, color:'#fca5a5', display:'flex', alignItems:'center', gap:8 }}>
              ⚠ {user.negligence_count} negligence flag{user.negligence_count !== 1 ? 's' : ''} recorded
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ManageAdminsInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('department');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selected, setSelected] = useState(null);
  const [limits, setLimits] = useState({ university: { current:0, max:MAX_UNIVERSITY_ADMINS }, departments: { total:0 } });

  useEffect(() => { fetchAdmins(); fetchLimits(); }, []);

  const fetchAdmins = async () => {
    try {
      const res = await api.get('/authority/admins');
      setAdmins(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const fetchLimits = async () => {
    try {
      const res = await api.get('/authority/admin-limits');
      setLimits(res.data);
    } catch (e) { /* silent */ }
  };

  const handleDelete = async (adminId, adminName, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete admin "${adminName}"?`)) return;
    try {
      await api.delete(`/authority/delete-admin/${adminId}`);
      setAdmins(admins.filter(a => a.id !== adminId));
      fetchLimits();
    } catch (e2) {
      setError(e2.response?.data?.error || 'Failed to delete admin');
    }
  };

  const deptAdmins = admins.filter(a => a.admin_type === 'department' && a.is_active !== false);
  const uniAdmins  = admins.filter(a => a.admin_type === 'university'  && a.is_active !== false);

  if (loading) return <LoadingScreen />;

  const tabStyle = (active) => ({
    flex:1, padding:'12px 20px', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:13, border:'none',
    background: active ? 'linear-gradient(135deg,#3b82f6,#2563eb)' : 'rgba(255,255,255,0.1)',
    color:'#fff', transition:'all 0.2s ease',
    boxShadow: active ? '0 4px 14px rgba(59,130,246,0.4)' : 'none',
  });

  const AdminCard = ({ admin }) => (
    <div className="ur-card-hover" onClick={() => setSelected(admin)}
      style={{ ...s.card, display:'flex', flexDirection:'column', gap:12, cursor:'pointer' }}>
      <div style={{ display:'flex', alignItems:'center', gap:14 }}>
        <div style={{ width:48, height:48, borderRadius:12, background: admin.admin_type === 'university' ? 'linear-gradient(135deg,#8b5cf6,#7c3aed)' : 'linear-gradient(135deg,#f59e0b,#d97706)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:800, color:'#fff', flexShrink:0 }}>
          {admin.name?.[0]?.toUpperCase() || 'A'}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#fff' }}>{admin.name}</div>
          <div style={{ fontSize:12, color:'rgba(255,255,255,0.6)', marginTop:2 }}>{admin.email}</div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:5 }}>
          <span style={{ padding:'3px 10px', borderRadius:6, fontSize:11, fontWeight:700, background: admin.is_active ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)', color: admin.is_active ? '#6ee7b7' : '#fca5a5', border:`1px solid ${admin.is_active ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
            {admin.is_active ? 'Active' : 'Inactive'}
          </span>
          {(admin.negligence_count > 0) && (
            <span style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:20, background: admin.negligence_count >= 3 ? 'rgba(239,68,68,0.25)' : 'rgba(245,158,11,0.2)', color: admin.negligence_count >= 3 ? '#fca5a5' : '#fcd34d', border:`1px solid ${admin.negligence_count >= 3 ? 'rgba(239,68,68,0.4)' : 'rgba(245,158,11,0.35)'}` }}>
              ⚠ {admin.negligence_count} negligence
            </span>
          )}
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:8 }}>
        {[
          ['Department', admin.admin_type === 'university' ? 'All Departments' : admin.department],
          ['Level', admin.admin_type === 'university' ? 'University' : 'Department'],
        ].map(([label, val]) => (
          <div key={label} style={{ padding:'8px 12px', borderRadius:8, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:0.6, marginBottom:2 }}>{label}</div>
            <div style={{ fontSize:12, color:'#fff', fontWeight:600 }}>{val}</div>
          </div>
        ))}
      </div>
      <button className="ur-btn-hover" style={{ ...s.btnDanger, width:'100%', textAlign:'center' }}
        onClick={e => handleDelete(admin.id, admin.name, e)}>
        Delete Admin
      </button>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.bgGrid} /><div style={s.bgOrb1} /><div style={s.bgOrb2} /><div style={s.bgOrb3} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar onToggle={() => setSidebarOpen(o => !o)} />}
        <div style={{ ...s.main, marginLeft:sidebarOpen ? 248 : 0, transition:'margin-left 0.3s ease' }}>
          <Topbar title="Manage Admins" subtitle={`${admins.length} total admins`}
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            actions={<button className="ur-btn-hover" style={s.btnSuccess} onClick={() => navigate('/authority/create-admin')}>+ Create Admin</button>}
          />
          <div style={s.content}>
            {error && <div style={s.alertErr}>{error}</div>}
            <PageHeader icon="👥" title="Manage Admins" subtitle="View and manage all admin accounts"
              stats={[
                { label:'Total Admins', value:admins.length },
                { label:'Department',   value:admins.filter(a => a.admin_type === 'department').length },
                { label:'University',   value:admins.filter(a => a.admin_type === 'university').length },
              ]}
              actions={<button className="ur-btn-hover" style={s.btnSuccess} onClick={() => navigate('/authority/create-admin')}>+ Create Admin</button>}
            />
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:20 }}>
              {[
                ['Total Admins', admins.length, COLORS.blue],
                ['Department', deptAdmins.length, COLORS.amber],
                [`University (${uniAdmins.length}/${MAX_UNIVERSITY_ADMINS})`, uniAdmins.length, COLORS.purple],
              ].map(([label, val, color]) => (
                <div key={label} className="ur-card-hover" style={{ ...s.card, textAlign:'center' }}>
                  <div style={{ fontSize:26, fontWeight:800, color:'#fff', marginBottom:4 }}>{val}</div>
                  <div style={{ fontSize:12, color:'rgba(255,255,255,0.7)' }}>{label}</div>
                  <div style={{ height:3, borderRadius:2, background:color, marginTop:10 }} />
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:10, marginBottom:20 }}>
              <button style={tabStyle(activeTab === 'department')} onClick={() => setActiveTab('department')}>Department Admins ({deptAdmins.length})</button>
              <button style={tabStyle(activeTab === 'university')} onClick={() => setActiveTab('university')}>University Admins ({uniAdmins.length}/{MAX_UNIVERSITY_ADMINS})</button>
            </div>
            {activeTab === 'department' && (
              deptAdmins.length === 0
                ? <EmptyState text="No department admins" sub="Create one to get started" />
                : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16 }}>
                    {deptAdmins.map(a => <AdminCard key={a.id} admin={a} />)}
                  </div>
            )}
            {activeTab === 'university' && (
              uniAdmins.length === 0
                ? <EmptyState text="No university admins" sub="Create one to get started" />
                : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16 }}>
                    {uniAdmins.map(a => <AdminCard key={a.id} admin={a} />)}
                  </div>
            )}
          </div>
        </div>
      </div>
      {selected && <ProfileModal user={selected} onClose={() => setSelected(null)} s={s} />}
    </div>
  );
}

export default function ManageAdmins() {
  return <ThemeProvider><ManageAdminsInner /></ThemeProvider>;
}
